import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { useFetchAppsInCollectionsQuery } from '../../redux/api/appMarket-api-slice';
import { PageInfo } from '../../components/Header/PageInfo';
import { useAppCollections } from '../../contexts/providers/collection';
import { FilterDropDown } from '../../components/Common/FilterDropDown';
import { Filters, getFilterSortInfoFromFilterKey } from '../../components/Common/FilterDropDown/filters';
import i18n from 'i18next';
import { readableFormat } from '../../utils/transformationHelper';
import { generateRoute } from '../../utils/location';
import { removeMatchingElementFromArray } from '../../utils/helpers';
import { AppsInCollectionParameters } from '../../redux/api/types';
import { AppMarketPageIdentifier } from '../../AppRoutes';
import { APP_GRID_LOAD_MORE_CONFIG, AppGrid } from '../../components/AppGrid';
import {SetPageTitle} from '../../components/Common/SetPageTitle';
import { useRatingsAndReviews } from '../../contexts/providers/ratings-reviews';

let defaultFilterKey = Filters.MostRelevant;

export const CollectionDetailsPage = (): JSX.Element => {
  let AllowedFilters: Filters[] =
    [Filters.MostRelevant,
      Filters.TopRated,
      Filters.MostPopular,
      Filters.Newest,
    ];
  const { collections, isLoading: collectionsLoading } = useAppCollections();
  const { t } = useTranslation();
  const ratingsAndReviewsFeatureFlag = useRatingsAndReviews();
  let history = useHistory();
  const { params: { collectionId } } = useRouteMatch<{ collectionId: string }>();
  let { filterKey } = useParams<{ filterKey: Filters }>();
  const [limit, setLimit] = useState<number>(APP_GRID_LOAD_MORE_CONFIG.maxEntriesPerPage as number + 1);

  let sort = useMemo(() => {
    return getFilterSortInfoFromFilterKey((filterKey) || defaultFilterKey);
  }, [filterKey]);

  const queryParams: AppsInCollectionParameters = {
    appCollectionUuid: collectionId,
    offset: 0,
    limit,
  };

  if (sort) queryParams.sorts = [sort];

  const {
    data: appsFromCollections,
    isLoading: appsInCollectionsLoading,
    isFetching: appsInCollectionFetching,
  } = useFetchAppsInCollectionsQuery(queryParams);

  const collection = useMemo(() => {
    return collections?.find(col => col.id === collectionId);
  }, [collectionId, collections]);

  const usLocale = i18n.language === 'en-US';
  const collectionDisplayName = readableFormat(collection?.displayName);
  const collectionName = collection?.displayName?.toLowerCase().replace(/ /g, '_').replace(/!$/, '');
  const headerText: string | undefined = usLocale ? collectionDisplayName : t('carousels.' + collectionName + '.display_name', collectionDisplayName);

  const changeLimit = (apiLimit: number) => {
    if (apiLimit !== limit) setLimit(apiLimit);
  };

  const filterChangeHandler = (filters: Filters) => {
    history.push(
      generateRoute(AppMarketPageIdentifier.Collections, [
        collectionId,
        filters,
      ]),
    );
  };

  if (!ratingsAndReviewsFeatureFlag.enabled) {
    removeMatchingElementFromArray<Filters>(AllowedFilters, Filters.TopRated);
    let topRatedFilter = Filters.TopRated;
    if (topRatedFilter === filterKey) history.replace(generateRoute(AppMarketPageIdentifier.Collections, [
      collectionId,
      defaultFilterKey,
    ]));
  }

  const noCollectionFound = !collection?.name && !collectionsLoading;
  return (
    <>
      {
        noCollectionFound ? <Redirect to={generateRoute(AppMarketPageIdentifier.Home)} /> :
          <div>
            <SetPageTitle title={headerText}/>
            <PageInfo hideDescription={true} title={headerText} />
            <FilterDropDown
              onChangeHandlerCallback={filterChangeHandler}
              allowedFilters={AllowedFilters}
              selectedFilter={(filterKey) || defaultFilterKey}
            />
           <AppGrid apps={appsFromCollections} isLoading={appsInCollectionsLoading} isFetching={appsInCollectionFetching} setApiLimit={changeLimit}/>
          </div>
      }
    </>
  );
};
